import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, ArrowLeft, Save, Loader2, Upload, Star, Mic, Users, Settings as SettingsIcon, Download, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { title: "", guest: "", guest_role: "", season_number: "01", episode_number: "", cover: "", embed_url: "", description: "", duration: "", featured: false, status: "published" };

function mediaUrl(src) {
  if (!src) return "";
  return src.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src;
}

function downloadCsv(filename, rows, columns) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const header = columns.map((c) => esc(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => esc(c.key === "created_at" ? new Date(r[c.key]).toLocaleString() : r[c.key])).join(",")).join("\n");
  const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const inputCls = "h-12 rounded-none bg-black/40 border-white/25 focus-visible:border-[#E30613] focus-visible:ring-0 focus-visible:ring-offset-0 text-white placeholder:text-neutral-600";
const Field = ({ label, children }) => (<div><p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">{label}</p>{children}</div>);

function EpisodesTab({ isAdmin }) {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/admin/podcast/episodes`); setEpisodes(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const openNew = () => { setForm({ ...EMPTY, status: isAdmin ? "published" : "draft" }); setEditingId(null); setError(""); setView("edit"); };
  const openEdit = (ep) => { setForm({ ...EMPTY, ...ep }); setEditingId(ep.id); setError(""); setView("edit"); };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/media/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, cover: data.path }));
    } catch { setError("Cover upload failed."); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    setError("");
    if (!form.title.trim()) { setError("Episode title is required."); return; }
    setSaving(true);
    try {
      const payload = { ...form, featured: !!form.featured };
      if (editingId) await axios.put(`${API}/podcast/episodes/${editingId}`, payload);
      else await axios.post(`${API}/podcast/episodes`, payload);
      await load(); setView("list");
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Save failed.");
    } finally { setSaving(false); }
  };

  const remove = async (ep) => {
    if (!window.confirm(`Delete "${ep.title}"?`)) return;
    await axios.delete(`${API}/podcast/episodes/${ep.id}`); await load();
  };

  if (view === "edit") {
    return (
      <div className="max-w-3xl" data-testid="podcast-episode-editor">
        <button onClick={() => setView("list")} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-neutral-400 hover:text-white transition-colors mb-6"><ArrowLeft size={16} /> Back to episodes</button>
        <h2 className="font-display uppercase text-3xl tracking-tight mb-6">{editingId ? "Edit Episode" : "New Episode"}</h2>
        <div className="space-y-4">
          <Field label="Episode title"><Input data-testid="ep-f-title" value={form.title} onChange={set("title")} className={inputCls} /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Guest"><Input data-testid="ep-f-guest" value={form.guest} onChange={set("guest")} placeholder="Guest name" className={inputCls} /></Field>
            <Field label="Guest role"><Input data-testid="ep-f-guestrole" value={form.guest_role} onChange={set("guest_role")} placeholder="Designer, Musician, Founder..." className={inputCls} /></Field>
            <Field label="Season #"><Input data-testid="ep-f-season" value={form.season_number} onChange={set("season_number")} placeholder="01" className={inputCls} /></Field>
            <Field label="Episode #"><Input data-testid="ep-f-epnum" value={form.episode_number} onChange={set("episode_number")} placeholder="01" className={inputCls} /></Field>
            <Field label="Duration"><Input data-testid="ep-f-duration" value={form.duration} onChange={set("duration")} placeholder="48 min" className={inputCls} /></Field>
          </div>
          <Field label="Embed link (YouTube / Spotify / Apple Podcasts) — plays inline">
            <Input data-testid="ep-f-embed" value={form.embed_url} onChange={set("embed_url")} placeholder="https://youtube.com/watch?v=... or https://open.spotify.com/episode/..." className={inputCls} />
          </Field>
          <Field label="Cover image">
            <div className="flex gap-2 items-center">
              <Input data-testid="ep-f-cover" value={form.cover} onChange={set("cover")} placeholder="https://... or upload" className={inputCls} />
              <label data-testid="ep-cover-upload" className="cursor-pointer shrink-0 inline-flex items-center gap-2 px-4 h-12 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/25 text-neutral-300 hover:text-white hover:border-white/60 transition-colors">
                {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} Upload
                <input type="file" accept="image/*" className="hidden" onChange={upload} />
              </label>
            </div>
            {form.cover && <img src={mediaUrl(form.cover)} alt="" className="mt-3 w-40 h-24 object-cover border border-white/10" />}
          </Field>
          <Field label="Description / show notes">
            <textarea data-testid="ep-f-description" value={form.description} onChange={set("description")} rows={3} placeholder="What this conversation is about..." className="w-full rounded-none bg-black/40 border border-white/25 focus:border-[#E30613] focus:outline-none text-white placeholder:text-neutral-600 p-3 text-sm" />
          </Field>
          <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="ep-f-featured">
            <input type="checkbox" checked={!!form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} className="w-4 h-4 accent-[#E30613]" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-neutral-300 inline-flex items-center gap-2"><Star size={13} className="text-[#E30613]" /> Feature as Latest Episode</span>
          </label>
          <div data-testid="ep-f-status">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Status</p>
            <div className="flex gap-2">
              {[["draft", "Draft"], ["published", "Published"]].map(([k, l]) => {
                const locked = k === "published" && !isAdmin;
                return <button key={k} type="button" disabled={locked} onClick={() => !locked && setForm((f) => ({ ...f, status: k }))} className={`px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors disabled:opacity-40 ${form.status === k ? "bg-[#E30613] border-[#E30613] text-white" : "border-white/25 text-neutral-400 hover:text-white"}`}>{l}</button>;
              })}
            </div>
            {!isAdmin && <p className="text-[10px] text-neutral-600 mt-2">Editors can save drafts. Only admins publish live.</p>}
          </div>
          {error && <p className="text-[#E30613] text-sm" data-testid="ep-f-error">{error}</p>}
          <button onClick={save} disabled={saving} data-testid="ep-f-save" className="inline-flex items-center gap-2 px-6 h-12 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} {editingId ? "Update Episode" : "Add Episode"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="podcast-episodes-tab">
      <div className="flex items-center justify-between gap-4 mb-6">
        <p className="text-neutral-400 text-sm">{episodes.length} episode(s) · {episodes.filter((e) => e.status === "published").length} live</p>
        <button onClick={openNew} data-testid="podcast-new-episode" className="inline-flex items-center gap-2 px-5 h-11 text-[10px] uppercase tracking-[0.2em] font-bold bg-white text-black hover:bg-neutral-200 transition-colors"><Plus size={15} /> New Episode</button>
      </div>
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#E30613]" size={28} /></div>
      ) : episodes.length === 0 ? (
        <p className="text-neutral-600 py-16 text-center border border-white/10">No episodes yet. Add the first conversation.</p>
      ) : (
        <div className="border border-white/10 divide-y divide-white/5" data-testid="podcast-episode-rows">
          {episodes.map((ep, i) => (
            <div key={ep.id} className="flex items-center gap-4 p-3" data-testid={`podcast-ep-row-${i}`}>
              <div className="w-14 h-10 shrink-0 border border-white/10 overflow-hidden bg-neutral-900 flex items-center justify-center">{ep.cover ? <img src={mediaUrl(ep.cover)} alt="" className="w-full h-full object-cover" /> : <Mic size={14} className="text-neutral-700" />}</div>
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-bold truncate">{ep.title} {ep.featured && <Star size={12} className="inline text-[#E30613]" fill="#E30613" />}</p>
                <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-neutral-500 truncate">{ep.guest ? `${ep.guest} · ` : ""}S{ep.season_number}{ep.episode_number ? ` · EP ${ep.episode_number}` : ""}</p>
              </div>
              <span className={`text-[10px] uppercase tracking-[0.15em] font-bold shrink-0 ${ep.published !== false ? "text-[#E30613]" : "text-neutral-600"}`}>{ep.published !== false ? "Live" : "Draft"}</span>
              <div className="flex items-center gap-3 shrink-0">
                <button onClick={() => openEdit(ep)} data-testid={`podcast-ep-edit-${i}`} title="Edit" className="text-neutral-500 hover:text-white"><Pencil size={16} /></button>
                <button onClick={() => remove(ep)} data-testid={`podcast-ep-delete-${i}`} title="Delete" className="text-neutral-500 hover:text-[#E30613]"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SignupsTab() {
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/admin/podcast/notify`); setSignups(data.signups || []); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cols = [{ key: "name", label: "Name" }, { key: "email", label: "Email" }, { key: "created_at", label: "Joined" }];

  return (
    <div data-testid="podcast-signups-tab">
      <div className="flex items-center justify-between gap-4 mb-6">
        <p className="text-neutral-400 text-sm">{signups.length} listener(s) waiting for episode one.</p>
        <div className="flex gap-2 shrink-0">
          <button onClick={load} data-testid="podcast-signups-refresh" className="inline-flex items-center gap-2 px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/20 text-neutral-400 hover:text-white transition-colors"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh</button>
          <button onClick={() => downloadCsv("vlmag-podcast-signups.csv", signups, cols)} disabled={signups.length === 0} data-testid="podcast-signups-export" className="inline-flex items-center gap-2 px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] font-bold bg-white text-black hover:bg-neutral-200 transition-colors disabled:opacity-50"><Download size={14} /> Export CSV</button>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#E30613]" size={28} /></div>
      ) : (
        <div className="border border-white/10" data-testid="podcast-signups-table">
          <table className="w-full text-left text-sm">
            <thead><tr className="border-b border-white/10">{cols.map((c) => <th key={c.key} className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] font-bold text-neutral-500">{c.label}</th>)}</tr></thead>
            <tbody>
              {signups.length === 0 ? (
                <tr><td colSpan={cols.length} className="px-4 py-10 text-center text-neutral-600">No signups yet.</td></tr>
              ) : signups.map((r, i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/5" data-testid={`podcast-signup-row-${i}`}>
                  <td className="px-4 py-3 text-neutral-300">{r.name || <span className="text-neutral-700">—</span>}</td>
                  <td className="px-4 py-3 text-neutral-300">{r.email}</td>
                  <td className="px-4 py-3 text-neutral-400">{new Date(r.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SettingsTab({ isAdmin }) {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/podcast`); setForm(data.settings); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setSaved(false); };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/media/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, hero_image: data.path })); setSaved(false);
    } finally { setUploading(false); e.target.value = ""; }
  };
  const save = async () => {
    setSaving(true);
    try { const { data } = await axios.post(`${API}/admin/podcast/settings`, form); setForm(data); setSaved(true); }
    finally { setSaving(false); }
  };

  if (loading || !form) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#E30613]" size={22} /></div>;

  return (
    <div className="max-w-2xl space-y-5" data-testid="podcast-settings-tab">
      <div className="border border-[#E30613]/40 bg-[#E30613]/10 text-neutral-200 text-xs px-4 py-3">
        Toggle <b>LIVE</b> to flip the public /podcast page from the "Coming Soon" teaser into the full episode hub.
      </div>
      <button onClick={() => isAdmin && setForm((f) => ({ ...f, live: !f.live }))} disabled={!isAdmin} data-testid="podcast-live-toggle"
        className={`inline-flex items-center gap-3 px-5 h-12 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors disabled:opacity-50 ${form.live ? "bg-[#E30613] border-[#E30613] text-white" : "border-white/25 text-neutral-300 hover:text-white"}`}>
        <span className={`w-2.5 h-2.5 rounded-full ${form.live ? "bg-white" : "bg-neutral-600"}`} /> Show is {form.live ? "LIVE" : "COMING SOON"}
      </button>

      <Field label="Hero image">
        <div className="flex gap-2 items-center">
          <Input data-testid="podcast-hero-input" value={form.hero_image} onChange={set("hero_image")} disabled={!isAdmin} placeholder="https://... or upload" className={inputCls} />
          <label data-testid="podcast-hero-upload" className="cursor-pointer shrink-0 inline-flex items-center gap-2 px-4 h-12 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/25 text-neutral-300 hover:text-white hover:border-white/60 transition-colors">
            {uploading ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />} Upload
            <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={!isAdmin} />
          </label>
        </div>
        {form.hero_image && <img src={mediaUrl(form.hero_image)} alt="" className="mt-3 w-full max-w-sm aspect-video object-cover grayscale-img border border-white/10" />}
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Season number"><Input data-testid="podcast-season-num" value={form.season_number} onChange={set("season_number")} disabled={!isAdmin} className={inputCls} /></Field>
        <Field label="Season title"><Input data-testid="podcast-season-title" value={form.season_title} onChange={set("season_title")} disabled={!isAdmin} className={inputCls} /></Field>
      </div>
      <Field label="Tagline"><Input data-testid="podcast-tagline" value={form.tagline} onChange={set("tagline")} disabled={!isAdmin} className={inputCls} /></Field>

      {isAdmin && (
        <button onClick={save} disabled={saving} data-testid="podcast-settings-save" className="inline-flex items-center gap-2 px-6 h-12 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} {saved ? "Saved" : "Save Settings"}
        </button>
      )}
      {!isAdmin && <p className="text-[10px] text-neutral-600">Only admins can change podcast settings.</p>}
    </div>
  );
}

const SUBTABS = [
  { key: "episodes", label: "Episodes", icon: Mic },
  { key: "signups", label: "Signups", icon: Users },
  { key: "settings", label: "Show Settings", icon: SettingsIcon },
];

export default function PodcastManager({ role }) {
  const isAdmin = (role || "admin") === "admin";
  const [sub, setSub] = useState("episodes");
  return (
    <div data-testid="podcast-manager">
      <div className="flex gap-2 mb-8 border-b border-white/10">
        {SUBTABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setSub(t.key)} data-testid={`podcast-subtab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-bold border-b-2 -mb-px transition-colors ${sub === t.key ? "border-[#E30613] text-white" : "border-transparent text-neutral-500 hover:text-white"}`}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>
      {sub === "episodes" && <EpisodesTab isAdmin={isAdmin} />}
      {sub === "signups" && <SignupsTab />}
      {sub === "settings" && <SettingsTab isAdmin={isAdmin} />}
    </div>
  );
}
